import {EventEmitter, Injectable} from '@angular/core';
import {CognitoUser} from 'amazon-cognito-identity-js';
import {Account, BusinessProfile} from '../model/account';
import {Auth} from 'aws-amplify';
import {Router} from '@angular/router';
import {Subscription, timer} from 'rxjs';
import * as jwt_decode from 'jwt-decode';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  static readonly BusinessProfileKey = 'iot4bos.business-profile';

  constructor(private router: Router) {
    this.newPasswordRequired = new EventEmitter();
    this.signedIn = new EventEmitter();
    this.businessProfileChanged = new EventEmitter();
    this.accountChanged = new EventEmitter();
  }

  newPasswordRequired: EventEmitter<CognitoUser>;
  signedIn: EventEmitter<CognitoUser>;
  businessProfileChanged: EventEmitter<BusinessProfile>;
  accountChanged: EventEmitter<Account>;
  refresher: Subscription;

  private static getTokenExpirationDate(token: string): Date {
    const decoded = jwt_decode(token);

    if (decoded.exp === undefined) {
      return null;
    }

    const date = new Date(0);
    date.setUTCSeconds(decoded.exp);
    return date;
  }

  signIn(username: string, password: string): Promise<void> {
    return Auth.signIn(username, password)
      .then(user => {
        if (user?.challengeName === 'NEW_PASSWORD_REQUIRED') {
          return this.newPasswordRequired.emit(user);
        }
        this.signedIn.emit(user);
      });
  }

  signOut(): Promise<boolean> {
    this.refresher.unsubscribe();
    return Auth.signOut().then(_ => {
      localStorage.removeItem(AuthService.BusinessProfileKey);
      return this.router.navigate(['/sign-in']);
    });
  }

  isAuthenticated(): Promise<boolean> {
    return Auth.currentAuthenticatedUser()
        .then(_ => Auth.currentSession())
        .then(_ => this.refresher = this.refresher || timer(0, 30 * 60 * 1000).subscribe(this.refreshToken))
        .then(_ => true)
        .catch(_ => false);
  }

  changePassword(user: CognitoUser, newPassword: string): Promise<void> {
    return Auth.completeNewPassword(user, newPassword, {})
      .then(_ => this.signedIn.emit(user));
  }

  selectBusinessProfile(businessProfile: BusinessProfile) {
    localStorage.setItem(AuthService.BusinessProfileKey, JSON.stringify(businessProfile));
    this.businessProfileChanged.emit(businessProfile);
  }

  refreshBusinessProfile() {
    const profile = new BusinessProfile(JSON.parse(localStorage.getItem(AuthService.BusinessProfileKey)));
    if (profile) {
      this.businessProfileChanged.emit(profile);
    }
  }

  signWithRefreshToken(federatedBusinessProfileId: string, federatedUserId: string,  token: string): Promise<boolean> {
    this.selectBusinessProfile(new BusinessProfile({ id: federatedBusinessProfileId }));

    return fetch('https://cognito-idp.' + environment.cognito.region + '.amazonaws.com/', {
        headers: {
            'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
            'Content-Type': 'application/x-amz-json-1.1',
        },
        mode: 'cors',
        cache: 'no-cache',
        method: 'POST',
        body: JSON.stringify({
            ClientId: environment.cognito.clientId,
            AuthFlow: 'REFRESH_TOKEN_AUTH',
            AuthParameters: {
                REFRESH_TOKEN: token
            }
        }),
    }).then((res) => {
        return res.json();
    }, (err) => {
      console.log(err);
      return false;
    }).then((data) => {
        localStorage.setItem(
          'CognitoIdentityServiceProvider.' + environment.cognito.clientId + '.' + federatedUserId + '.accessToken',
          data.AuthenticationResult.AccessToken);
        localStorage.setItem(
          'CognitoIdentityServiceProvider.' + environment.cognito.clientId + '.' + federatedUserId + '.refreshToken', token);
        localStorage.setItem(
          'CognitoIdentityServiceProvider.' + environment.cognito.clientId + '.' + federatedUserId + '.idToken',
          data.AuthenticationResult.IdToken);
        localStorage.setItem(
          'CognitoIdentityServiceProvider.' + environment.cognito.clientId + '.LastAuthUser',
          federatedUserId);
        return true;
    }, (err) => {
      console.log(err);
      return false;
    });

  }

  private refreshToken() {
    Auth.currentAuthenticatedUser()
      .then(authenticated => {
        if (!authenticated) {
          return this.signOut();
        }

        const expiration: Date = AuthService.getTokenExpirationDate(
          authenticated
            .getSignInUserSession()
            .getIdToken()
            .getJwtToken()
        );
        const now = (new Date().getTime() + 1800000).valueOf();
        const tokenExpired = !(expiration.valueOf() > now);
        if (tokenExpired) {
          authenticated.refreshSession(authenticated.getSignInUserSession().getRefreshToken(), (error) => {
            if (error) {
              this.signOut();
            }
          });
        }
      })
      .catch(_ => {
        this.signOut();
      });
  }
}
